import { captureException } from "@sentry/react";
import type { SignInResult } from "Infrastructure/Api/Api";
import type { NavigateFunction } from "Navigation/useNavigate";
import { setUnauthenticatedUrl } from "State/Auth/AuthReducer";
import type { AppUser } from "State/Auth/Models/AuthStateModels";
import { getCodeListCollectionAsync } from "State/CodeLists/CodeListReducer";
import type { RootStateType } from "State/Store";
import { setToken } from "Utils/Storage/LocalStorage/LocalStorageUtils";
import { trackUserIdentification } from "Utils/TrackingUtils";
import { getAppUrl, resolveStartPage } from "Utils/UrlUtils";
import { isAfter, parseJSON } from "date-fns";
import { put, select } from "typed-redux-saga";
import { queryClient } from "Infrastructure/ReactQuery/queryClient";
import { prefetchUserInfo } from "Auth/Api/Queries/useUserInfoQuery";

export const JWT_KEY = "jwtToken";

export function isAuthenticated(user: AppUser | null) {
  if (user === null) {
    return false;
  }

  return isAfter(parseJSON(user.tokenExpiration), new Date());
}

export function* afterSignInSuccess(
  response: SignInResult,
  navigate?: NavigateFunction,
) {
  const appUrl = getAppUrl();
  setToken(response);

  // React Query prefetch (for new consumers using useUserInfoQuery)
  prefetchUserInfo(queryClient);

  yield put(getCodeListCollectionAsync.request());
  trackUserIdentification();

  const { user, unauthenticatedUrl } = yield* select(
    (state: RootStateType) => state.auth,
  );
  yield* put(setUnauthenticatedUrl(null));

  try {
    const startUrl = resolveStartPage(user, unauthenticatedUrl);

    if (navigate) {
      const url = new URL(
        `${window.location.protocol}//${window.location.host}${startUrl}`,
      );

      navigate(
        {
          pathname: url.pathname,
          search: url.search,
        },
        {
          replace: true,
          state: null,
          relative: "path",
          preventScrollReset: true,
        },
      );
    }
  } catch (e) {
    captureException(e);

    if (navigate) {
      const url = appUrl("sign-in");

      navigate(
        {
          pathname: url,
        },
        {
          replace: true,
          state: null,
          relative: "path",
          preventScrollReset: true,
        },
      );
    }
  }
}
